import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Star, Edit3 } from "lucide-react";
import { useMember } from "@/components/MemberProvider";
import { NoteDialog } from "@/components/NoteDialog";
import { FormHistory } from "@/components/FormHistory";
import { FormStats } from "@/components/FormStats";
import { API, fmtDate, slug } from "@/lib/api";

const FavCard = ({ horse }) => {
  const { notes, toggleFav } = useMember();
  const [runs, setRuns] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { axios.get(`${API}/horses/${encodeURIComponent(horse)}`).then((r) => setRuns(r.data)); }, [horse]);
  const latest = runs[0];

  return (
    <div className="rise min-w-0 rounded-2xl bg-[#131C31] border border-white/5 p-5" data-testid={`fav-card-${slug(horse)}`}>
      <div className="flex items-center gap-2">
        <h3 className="font-display text-xl font-bold mr-auto">{horse}</h3>
        <button data-testid={`fav-note-${slug(horse)}`} onClick={() => setOpen(true)} className="p-2 rounded-full hover:bg-white/10"><Edit3 className="w-4 h-4 text-slate-400" /></button>
        <button data-testid={`fav-remove-${slug(horse)}`} onClick={() => toggleFav(horse)} className="p-2 rounded-full hover:bg-white/10"><Star className="w-4 h-4 fill-amber-400 text-amber-400" /></button>
      </div>
      {runs.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {runs.map((x) => (
            <Link key={`${x.meeting_id}-${x.race_number}`} data-testid={`fav-run-${x.meeting_id}-${x.race_number}`} to={`/meeting/${x.meeting_id}?race=${x.race_number}`} className="text-xs px-2 py-1 rounded-full bg-blue-600/20 text-blue-300 border border-blue-500/30 hover:bg-blue-600/30">
              {fmtDate(x.date)} · {x.track} R{x.race_number}
            </Link>
          ))}
        </div>
      )}
      {notes[horse] && <p className="mt-3 text-sm text-amber-200/90 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 whitespace-pre-wrap">{notes[horse]}</p>}
      {latest && (
        <div className="mt-3 space-y-2">
          {latest.runner.last20 && <p className="text-xs text-slate-400">Last 20 starts: <span className="font-mono tracking-widest text-slate-200">{latest.runner.last20}</span></p>}
          <FormStats stats={latest.runner.form_stats} testid={`fav-stats-${slug(horse)}`} />
          <FormHistory starts={latest.runner.last_starts} live={!!latest.runner.form_stats} />
        </div>
      )}
      <NoteDialog horse={horse} open={open} onOpenChange={setOpen} />
    </div>
  );
};

export default function Favourites() {
  const { favs } = useMember();
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="text-4xl sm:text-5xl font-black uppercase tracking-tight mb-2">My Stable</h1>
      <p className="text-slate-400 mb-8">Horses you follow, their upcoming runs and your private notes.</p>
      {favs.length === 0 ? (
        <div data-testid="favourites-empty" className="rounded-2xl border border-dashed border-white/10 p-10 text-center text-slate-400">
          Tap the star next to any runner to follow it here.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4" data-testid="favourites-list">{favs.map((h) => <FavCard key={h} horse={h} />)}</div>
      )}
    </div>
  );
}
